import { Controller } from "@hotwired/stimulus"
import { TabulatorFull as Tabulator } from "tabulator-tables"
import { DateTime } from "luxon"

export default class extends Controller {
  static targets = ["filter", "table", "datepicker"]

  // Creates a table and loads content via Ajax
  // Columns are are auto-generated from the JSON response
  connect() {
    if (document.documentElement.hasAttribute("data-turbo-preview")) return

    // Create columns with according formatter type
    // TODO: Make sorting more elegant, Should be set in markup
    let columns = []
    JSON.parse(this.data.get("cols")).forEach((element) => {
      // Default sort by string
      let sorter = "string",
        sorterParams = {}
      const locale = new URL(window.location.href).pathname.split("/")[1]
      const formats = [
        "d. LLLL yyyy T",
        "d. LLLL yyyy TT",
        "d. LLLL yyyy",
        "d. LLLL yyyy, EEEE"
      ]

      const dateSorter = function (
        a,
        b,
        aRow,
        bRow,
        column,
        dir,
        sorterParams
      ) {
        let dateA, dateB, currentFormat

        for (let format of formats) {
          dateA = DateTime.fromFormat(a, format, { locale: locale })
          if (dateA.isValid) {
            currentFormat = format
            break
          }
        }

        if (!currentFormat) return

        dateB = DateTime.fromFormat(b, currentFormat, { locale: locale })
        return dateA - dateB
      }

      // Some colums are sorted by date
      if (
        [
          "updated_at",
          "start_at",
          "end_at",
          "ordered_at",
          "date",
          "valid_from",
          "valid_to"
        ].includes(element.field)
      ) {
        sorter = dateSorter
        // The column Deposit -> identifier is sorted by custom sorter
      } else if (
        [
          "deposit_identifier",
          "receipt_identifier",
          "invoice_identifier"
        ].includes(element.field)
      ) {
        sorter = function (a, b, aRow, bRow, column, dir, sorterParams) {
          // Legacy identifiers
          if (!isNaN(parseInt(a)) || !isNaN(parseInt(b))) {
            return a.localeCompare(b)

            // New identifiers (e.g. "Deposit 12/2022")
            // Zero padding is important, because 2022100 > 20231 and with
            // padding: 202200100 < 202300001
          } else {
            let identifierA = Number(
              a.match(/\/(\d*)/)[1] + a.match(/\s(\d*)/)[1].padStart(5, "0")
            )
            let identifierB = Number(
              b.match(/\/(\d*)/)[1] + b.match(/\s(\d*)/)[1].padStart(5, "0")
            )

            return identifierA - identifierB
          }
        }
      } else if (
        ["duration", "balance", "saldo", "in_balance", "out_balance"].includes(
          element.field
        )
      ) {
        sorter = function (a, b, aRow, bRow, column, dir, sorterParams) {
          let durationA = Number(
            a.split("h")[0].replace(".", "").replace(",", ".")
          )
          let durationB = Number(
            b.split("h")[0].replace(".", "").replace(",", ".")
          )
          return durationA - durationB
        }

        // Some colums are sorted by number
      } else if (
        ["tickets", "assigned_by", "quantity", "row_order"].includes(element.field)
      ) {
        sorter = "number"
      }

      const formatter = element.markup === true ? "html" : "plaintext"

      // Insert column into table
      columns.push({
        title: element.title,
        field: element.field,
        formatter: formatter,
        width: element.width,
        sorter: sorter,
        sorterParams: sorterParams,
        visible: this.data.get("group") != element.field,
        headerSort: this.data.has("no-sort") == false
      })
    })

    // Fill columns with data
    const remote = this.data.get("remote") === "true"
    const id = "#" + this.tableTarget.getAttribute("id")
    this.table = new Tabulator(id, {
      columns: columns,
      pagination: true,
      paginationMode: remote ? "remote" : "local",
      paginationSize: this.data.get("pagination") || 25,
      ajaxURL: this.data.get("content-url") + (remote ? "?page=1" : ""),
      filterMode: remote ? "remote" : "local",
      sortMode: remote ? "remote" : "local",
      placeholder: this.data.get("placeholder") || "Keine Daten",
      layout: "fitColumns",
      selectableRows: false,
      groupBy: this.data.get("group"),
      groupToggleElement: "header",
      printAsHtml: true,
      printStyled: true,
      htmlOutputConfig: { rowGroups: true },

      // We want to be able to disable sorting and set the direction
      initialSort: [
        this.data.has("no-sort")
          ? "false"
          : {
              column: columns[0].field,
              dir: this.data.get("sort-dir") || "asc"
            }
      ]
    })

    // Hide pagination if not needed
    // Also consider tables with grouping
    this.table.on("dataLoaded", function (data) {
      let table = this
      setTimeout(function () {
        let rows
        if (remote) {
          rows = table.getPageSize() * table.getPageMax()
        } else {
          rows = Math.max(
            ...table.rowManager.displayRows.map((el) => el.length)
          )
        }
        table.footerManager.element.classList.toggle(
          "d-none",
          rows <= table.getPageSize()
        )
      }, 1)
    })

    // Handle redirection on session timeout
    this.table.on("dataLoadError", function (error) {
      if (error.statusText == "Unauthorized") {
        location.reload()
      }
    })

    // Init tables with filters
    this.filter()
  }

  // Filters the table by the specified column
  // A filter can either be an input field or a select field
  filter() {
    let filters = []
    this.filterTargets.forEach((target) => {
      let value = "",
        type = ""

      // Filter type dropdown
      if (target.tagName == "SELECT") {
        value = target[target.selectedIndex].value
        if (target.getAttribute("data-mode") == "like") type = "like"
        else if (target.getAttribute("data-mode") == "regex") type = "regex"
        else type = "="

        // Filter type input
      } else {
        value = target.value
        type = "like"
      }

      // Apply filter
      if (value != "") {
        filters.push(
          target
            .getAttribute("data-field")
            .split(",")
            .map((target) => {
              return {
                field: target,
                type: type,
                value: value
              }
            })
        )
      }
    })

    // Do nothing if filters are already set to those values
    if (JSON.stringify(this.filters) == JSON.stringify(filters)) return
    this.filters = filters

    // Trigger tabulator filter
    this.table.setFilter(filters)
  }

  // Resets the datepicker filter
  resetDatepicker(event) {
    this.datepickerTarget._flatpickr.clear()
  }

  // Updates orders table whenever the date filter changed
  // Nice thing is tabulatorjs takes care of synching with all other filters
  updatedTable(event) {
    const [data, status, xhr] = event.detail
    const response = JSON.parse(xhr.response)
    this.table.setData(response)
  }

  formatDateForFilename(date) {
    const year = date.getFullYear()
    const month = (date.getMonth() + 1).toString().padStart(2, '0')
    const day = date.getDate().toString().padStart(2, '0')
    return `${year}-${month}-${day}`
  }

  export(baseUrl, format) {
    const selectedDates = document.querySelector('[data-table-target=datepicker]')._flatpickr.selectedDates
    const formattedDates = selectedDates.map(this.formatDateForFilename).join('_')
    let filename = ["Tresorbewegungen", formattedDates].filter(Boolean).join('_') + '.' + format

    let acceptHeader
    if (format == 'xls') {
      acceptHeader = 'application/vnd.ms-excel'
    }
    if (format == 'csv') {
      acceptHeader = 'text/csv'
    }

    let filteredRows = this.table.getRows("active");
    let filteredIds = filteredRows.map(row => row.getData().id)
    let params = new URLSearchParams()
    filteredIds.forEach(id => params.append('id[]', id))

    const url = `${baseUrl}`
    const csrfToken = document.querySelector('meta[name="csrf-token"]').getAttribute('content');

    fetch(url, {
      method: 'POST',
      headers: {
      'Accept': acceptHeader,
      'Content-Type': 'application/x-www-form-urlencoded',
      'X-CSRF-Token': csrfToken
      },
      body: params.toString()
    })
    .then(response => {
      if (response.ok) {
        return response.blob()
      } else {
        throw new Error('Response was not OK')
      }
    })
    .then(blob => {
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.style.display = 'none'
      a.href = url
      a.download = filename
      document.body.appendChild(a)
      a.click()
      window.URL.revokeObjectURL(url)
    })
    .catch(error => console.error('Error:', error))
  }
}
